'use client'

import { ChangeEvent, FC } from 'react'

export interface PCVideoControlsProps {
  isMuted: boolean
  playbackRate: number
  currentTime: number
  duration: number
  onToggleMute: () => void
  onCyclePlaybackRate: () => void
  onSeek: (time: number) => void
  formatTime: (time: number) => string
}

/**
 * PC モーダルの動画下端コントロール帯。
 * 動画個別ページ（video-detail-client/VideoPlayer.tsx の PC 用コントロール）と同じ構成:
 * 上段＝ミュート＋再生速度ボタン（常時表示・タップで 1x→1.2x→1.5x→2x を巡回）、
 * 下段＝シークバー＋「経過 / 総尺」。
 *
 * 埋め込み先へ配信する tailwind.css はキャッシュされ得るため、他の埋め込み
 * コンポーネント同様に <style> で自前の CSS を持つ。
 */
const PCVideoControls: FC<PCVideoControlsProps> = ({
  isMuted,
  playbackRate,
  currentTime,
  duration,
  onToggleMute,
  onCyclePlaybackRate,
  onSeek,
  formatTime,
}) => {
  const handleSeek = (e: ChangeEvent<HTMLInputElement>) => {
    onSeek(parseFloat(e.target.value))
  }

  return (
    <>
      <style>{`
        .pc-video-controls {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 12px;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0));
          z-index: 5;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .pc-video-controls-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pc-mute-button {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          padding: 0;
          cursor: pointer;
          color: rgba(255, 255, 255, 0.8);
          transition: color 150ms ease;
        }
        .pc-mute-button:hover {
          color: #fff;
        }

        .pc-speed-button {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.5);
          border: none;
          border-radius: 50%;
          padding: 0;
          cursor: pointer;
          color: #fff;
          font-size: 12px;
          font-weight: 500;
          line-height: 1;
          transition: background 150ms ease;
        }
        .pc-speed-button:hover {
          background: rgba(0, 0, 0, 0.7);
        }

        .pc-seek-bar {
          flex: 1;
          height: 4px;
          margin: 0;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 9999px;
          appearance: none;
          -webkit-appearance: none;
          cursor: pointer;
          outline: none;
        }
        .pc-seek-bar::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 10px;
          height: 10px;
          background: #fff;
          border: none;
          border-radius: 50%;
        }
        .pc-seek-bar::-moz-range-thumb {
          width: 10px;
          height: 10px;
          background: #fff;
          border: none;
          border-radius: 50%;
        }
        .pc-seek-bar::-moz-range-track {
          height: 4px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 9999px;
        }

        .pc-time-label {
          color: rgba(255, 255, 255, 0.8);
          font-size: 12px;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
      `}</style>

      {/* 動画クリック（再生/停止）へ伝播させない */}
      <div className="pc-video-controls" onClick={(e) => e.stopPropagation()}>
        <div className="pc-video-controls-row">
          <button
            type="button"
            className="pc-mute-button"
            onClick={onToggleMute}
            aria-label={isMuted ? 'ミュートを解除' : 'ミュートにする'}
          >
            {isMuted ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="pc-speed-button"
            onClick={onCyclePlaybackRate}
            aria-label="再生速度を変更"
          >
            {playbackRate}x
          </button>
        </div>
        <div className="pc-video-controls-row">
          <input
            type="range"
            className="pc-seek-bar"
            min={0}
            max={duration || 100}
            step="any"
            value={currentTime}
            onChange={handleSeek}
            aria-label="再生位置"
          />
          <span className="pc-time-label">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      </div>
    </>
  )
}

export default PCVideoControls
